import { X } from 'lucide-react';
import Button from '../../../components/Button';
import type { JourneyItemTemplateFirestore, JourneyItemType } from '../services/journeyTypes';
import {
    ChatJourneyItemForm,
    CheckInJourneyItemForm,
    CommunityQuestionJourneyItemForm,
    LessonJourneyItemForm,
    StoryJourneyItemForm,
    ToolJourneyItemForm,
} from './journey-item-forms';

interface EditJourneyItemProps {
    itemTemplate?: JourneyItemTemplateFirestore;
    itemType: JourneyItemType;
    initialOrder: number;
    onSave: (item: JourneyItemTemplateFirestore) => void;
    onSaveAndContinue: (item: JourneyItemTemplateFirestore) => void;
    onCancel: () => void;
    sectionHeader: string;
}

export const EditJourneyItem = ({ itemTemplate, itemType, initialOrder, onSave, onSaveAndContinue, onCancel, sectionHeader }: EditJourneyItemProps) => {
    const currentItemType: JourneyItemType = itemTemplate?.type ?? itemType;
    const currentOrder = itemTemplate?.order ?? initialOrder;
    const isEditMode = !!itemTemplate?.id;

    const renderForm = () => {
        switch (currentItemType) {
            case 'chat':
                return (
                    <ChatJourneyItemForm
                        itemTemplate={itemTemplate?.type === 'chat' ? itemTemplate : undefined}
                        initialOrder={currentOrder}
                        onSave={onSave}
                        onSaveAndContinue={onSaveAndContinue}
                        onCancel={onCancel}
                    />
                );
            case 'check-in':
                return (
                    <CheckInJourneyItemForm
                        itemTemplate={itemTemplate?.type === 'check-in' ? itemTemplate : undefined}
                        initialOrder={currentOrder}
                        onSave={onSave}
                        onSaveAndContinue={onSaveAndContinue}
                        onCancel={onCancel}
                    />
                );
            case 'story':
                return (
                    <StoryJourneyItemForm
                        itemTemplate={itemTemplate?.type === 'story' ? itemTemplate : undefined}
                        initialOrder={currentOrder}
                        onSave={onSave}
                        onSaveAndContinue={onSaveAndContinue}
                        onCancel={onCancel}
                    />
                );
            case 'lesson':
                return (
                    <LessonJourneyItemForm
                        itemTemplate={itemTemplate?.type === 'lesson' ? itemTemplate : undefined}
                        initialOrder={currentOrder}
                        onSave={onSave}
                        onSaveAndContinue={onSaveAndContinue}
                        onCancel={onCancel}
                    />
                );
            case 'community-question':
                return (
                    <CommunityQuestionJourneyItemForm
                        itemTemplate={itemTemplate?.type === 'community-question' ? itemTemplate : undefined}
                        initialOrder={currentOrder}
                        onSave={onSave}
                        onSaveAndContinue={onSaveAndContinue}
                        onCancel={onCancel}
                    />
                );
            case 'tool':
                return (
                    <ToolJourneyItemForm
                        itemTemplate={itemTemplate?.type === 'tool' ? itemTemplate : undefined}
                        initialOrder={currentOrder}
                        onSave={onSave}
                        onSaveAndContinue={onSaveAndContinue}
                        onCancel={onCancel}
                    />
                );
            default:
                return <div className="text-red-600">Unsupported item type: {currentItemType}</div>;
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-600/50 z-50 flex items-center justify-center p-4">
            <div className="relative mx-auto w-full max-w-screen-xl max-h-full shadow-lg rounded-md bg-white flex flex-col">
                <div className="flex items-center justify-between gap-2 p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
                    <div className="min-w-0">
                        <h3 className="text-lg font-medium text-gray-900">
                            {isEditMode ? 'Edit Journey Item' : 'Add Journey Item'}
                        </h3>
                        <p className="text-sm text-gray-500 break-words">Section: {sectionHeader}</p>
                    </div>
                    <Button
                        onClick={onCancel}
                        variant="ghost"
                        icon={<X size={24} />}
                    />
                </div>

                <div className="overflow-y-auto flex-1 p-4 sm:p-6">
                    {renderForm()}
                </div>
            </div>
        </div>
    );
};
